/**
 * Key center regions for chord charts.
 *
 * A chart has one `globalKeyCenter` covering the whole thing, and a list of
 * regions that override it over stretches of the chart. A region runs from its
 * start chord until the next region begins, or the chart ends. Chords before
 * the first region, or anywhere no region covers, inherit the global key.
 *
 * Everything here is anchored to stable chord IDs, never to list positions or
 * timestamps: a region has to mean the same stretch of music after chords
 * around it are added and deleted. Bar lengths in the UI are derived from the
 * chords a region covers and are never stored.
 *
 * Separators are entries in the same list but are not chords. They carry no key
 * and never anchor a region; a separator simply sits inside whichever region
 * spans it, so deleting or moving one never disturbs an anchor. Everything here
 * reads chords only.
 *
 * The functions are pure and take the chart entries they need, so the edit
 * rules can be tested without rendering a chart. See KEY-CENTER-TRACKING-SPEC.md
 * for the design these implement.
 */

/**
 * Mode 0 is major, 1 is minor — the convention used throughout the app.
 *
 * `spelling` records which name a black key was chosen by, since G♭ and F♯ are
 * one pitch but not one thing on a chart, and which is right depends on the
 * song rather than on any rule the app could apply for you. It is display only:
 * it never takes part in comparing two key centers, so a region spelled G♭ and
 * one spelled F♯ are the same key and merge like any other pair.
 */
export type KeyCenter = {
  pitchClass: number;
  mode: 0 | 1;
  spelling?: 'sharp' | 'flat';
};

/**
 * Where a region came from. Manual regions are the user's own and are never
 * merged or dropped by automation; auto regions are disposable and
 * recomputed after edits (a MIDI file's analysis, and the chart's keys taken
 * from it). A chart linked to a MIDI file follows the file's changes wherever
 * it matched the file, manual or not (chartFileLink.ts).
 */
export type KeyCenterSource = 'manual' | 'auto';

export type KeyCenterRegion = {
  /**
   * The key from this chord on, or null for "no local key from here" — the
   * marker that ends the region before it and hands the rest of the chart back
   * to the global key. Without it a local key could only be ended by starting
   * another one, and a chart whose middle section has no key of its own would
   * have no way to say so.
   */
  keyCenter: KeyCenter | null;
  startChordId: string;
  source: KeyCenterSource;
};

/** All this module needs of a chart entry. */
export type ChartEntryRef = {
  id: string;
  isSeparator: boolean;
};

/**
 * Two key centers are equal only if the pitch class and the mode both match,
 * which is what decides whether adjacent regions merge: a C major region never
 * absorbs a C minor one, while a G♭ region and an F♯ region are one key spelled
 * two ways and do merge.
 */
export function keyCentersEqual(a: KeyCenter | null, b: KeyCenter | null): boolean {
  if (a === null || b === null) return a === b;
  return a.pitchClass === b.pitchClass && a.mode === b.mode;
}

/** The chart's chord IDs in order, with separators ignored. */
export function chordIdsInOrder(entries: ChartEntryRef[]): string[] {
  return entries.filter(entry => !entry.isSeparator).map(entry => entry.id);
}

/**
 * Regions in chart order, dropping any whose anchor is not a chord in the
 * chart. Order is derived from the chart rather than trusted from the list,
 * for the same reason anchors are IDs: only the chart knows what comes first.
 */
export function regionsInChartOrder(
  entries: ChartEntryRef[],
  regions: KeyCenterRegion[]
): KeyCenterRegion[] {
  const position = new Map<string, number>();
  chordIdsInOrder(entries).forEach((id, index) => position.set(id, index));

  return regions
    .filter(region => position.has(region.startChordId))
    .sort((a, b) => position.get(a.startChordId)! - position.get(b.startChordId)!);
}

/**
 * The effective key center for one chord: the region covering it, or the
 * global key when no region does. Returns null only when the chart has no
 * global key either.
 *
 * A chord ID that is not in the chart gets the global key — a caller asking
 * about a chord that just went away should not get a region's key by accident.
 */
export function keyCenterForChord(
  entries: ChartEntryRef[],
  regions: KeyCenterRegion[],
  globalKeyCenter: KeyCenter | null,
  chordId: string
): KeyCenter | null {
  const covering = regionCoveringChord(entries, regions, chordId);
  // A region with no key of its own is the marker that ends the one before it,
  // so the chords it covers fall back to the global key like any uncovered ones.
  if (covering === null || covering.keyCenter === null) return globalKeyCenter;
  return covering.keyCenter;
}

/**
 * The region covering one chord — the last one starting at or before it — or
 * null when none does.
 *
 * Distinct from keyCenterForChord, which answers what key the chord is in: this
 * says whether a region has anything to say about it at all, which is what a
 * caller needs to know before offering to end one.
 */
export function regionCoveringChord(
  entries: ChartEntryRef[],
  regions: KeyCenterRegion[],
  chordId: string
): KeyCenterRegion | null {
  const chordIds = chordIdsInOrder(entries);
  const chordPosition = chordIds.indexOf(chordId);
  if (chordPosition === -1) return null;

  const ordered = regionsInChartOrder(entries, regions);
  let covering: KeyCenterRegion | null = null;
  for (const region of ordered) {
    if (chordIds.indexOf(region.startChordId) > chordPosition) break;
    covering = region;
  }
  return covering;
}

/**
 * Each region paired with the chords it covered, in the chart as it was: its
 * own chords, from its anchor up to where the next region started.
 *
 * This is what keeps a region from wandering into its neighbour when its anchor
 * has to move — an anchor may only land on a chord from its own territory.
 */
function regionTerritories(
  prevEntries: ChartEntryRef[],
  regions: KeyCenterRegion[]
): { region: KeyCenterRegion; territory: string[] }[] {
  const prevChordIds = chordIdsInOrder(prevEntries);
  const ordered = regionsInChartOrder(prevEntries, regions);

  return ordered.map((region, index) => {
    const start = prevChordIds.indexOf(region.startChordId);
    const nextRegion = index + 1 < ordered.length ? ordered[index + 1] : null;
    const end = nextRegion === null
      ? prevChordIds.length
      : prevChordIds.indexOf(nextRegion.startChordId);
    return { region, territory: prevChordIds.slice(start, end) };
  });
}

/**
 * Merges adjacent auto regions that carry the same key center, keeping the
 * earlier one — its territory simply extends through the later one — and drops
 * an auto "no local key" marker with nothing before it to end.
 *
 * Only the auto layer: it is recomputed, so two auto regions in one key say
 * nothing the first does not. A manual region is the user's and is only ever
 * removed by the user (clearKeyCenterAtChord), even when it repeats the key
 * before it, and a manual "none" stays where it was put even with nothing
 * before it, so a key set earlier later still ends there.
 */
function mergeAdjacentRegions(ordered: KeyCenterRegion[]): KeyCenterRegion[] {
  const merged: KeyCenterRegion[] = [];
  for (const region of ordered) {
    const previous = merged.length > 0 ? merged[merged.length - 1] : null;
    if (region.source === 'auto') {
      if (previous === null && region.keyCenter === null) continue;
      if (previous !== null && previous.source === 'auto' && keyCentersEqual(previous.keyCenter, region.keyCenter)) continue;
    }
    merged.push(region);
  }
  return merged;
}

/**
 * Brings the region list back in line with the chart after an edit.
 *
 * Call this after any edit that adds or removes chords, passing the entries as
 * they were and as they now are. The rules it applies:
 *
 * - An interior chord going away shrinks its region and changes no key.
 * - A region's first chord going away slides the anchor to the next surviving
 *   chord *of that same region*, never into the next region's territory.
 * - A region that loses every one of its chords is removed.
 * - Adjacent auto regions that end up with the same key merge; manual ones
 *   are kept.
 * - A chord inserted at the very start of the chart joins the region that
 *   began there, rather than falling back to the global key. This is the one
 *   insert that needs help: a chord inserted anywhere else already falls inside
 *   whichever region covers the chord to its left, with no anchor to move.
 *
 * Nothing is left dangling: every anchor in the result is a chord that exists
 * in `nextEntries`.
 */
export function reconcileRegions(
  prevEntries: ChartEntryRef[],
  nextEntries: ChartEntryRef[],
  regions: KeyCenterRegion[]
): KeyCenterRegion[] {
  if (regions.length === 0) return regions;

  const prevChordIds = chordIdsInOrder(prevEntries);
  const nextChordIds = chordIdsInOrder(nextEntries);
  if (nextChordIds.length === 0) return [];

  const surviving = new Set(nextChordIds);

  const slid: KeyCenterRegion[] = [];
  regionTerritories(prevEntries, regions).forEach(({ region, territory }) => {
    const anchor = territory.find(id => surviving.has(id));
    if (anchor !== undefined) slid.push({ ...region, startChordId: anchor });
  });

  if (slid.length === 0) return [];

  // A region that started at the chart's first chord keeps starting there when
  // chords are inserted ahead of it, so an inserted chord inherits from its
  // right at the top of the chart.
  const firstRegion = slid[0];
  const startedAtChartStart = prevChordIds.length > 0 &&
    prevChordIds.indexOf(firstRegion.startChordId) === 0;
  if (startedAtChartStart && firstRegion.startChordId !== nextChordIds[0]) {
    slid[0] = { ...firstRegion, startChordId: nextChordIds[0] };
  }

  return mergeAdjacentRegions(regionsInChartOrder(nextEntries, slid));
}

/**
 * Brings the region list back in line after chords are moved.
 *
 * A move is a delete and an insert as far as the chords go, but the keys do not
 * follow the chords, with one exception:
 *
 * - Move part of a region and **the key stays behind**. The moved chords are
 *   treated as deleted where they came from, so the anchor slides to the first
 *   chord of that region that did not travel; at the destination the chords
 *   join whatever region they land in. Swapping two chords inside one region
 *   therefore changes nothing, which is the point.
 * - Move a region **completely** — every chord from its first through its last
 *   — and the region travels with it, keeping its key and its manual/auto
 *   status. This is what makes swapping a verse and a chorus do the obvious
 *   thing.
 *
 * `movedChordIds` is the selection that travelled. A region is complete when
 * every chord it covered is in that set; a selection covering only part of a
 * region is a partial move, however large it is.
 *
 * Copy and paste needs nothing here: pasted entries get new IDs, so they anchor
 * nothing and fall into whichever region receives them. Carrying keys along
 * with a copied group would be an opt-in on top of that, and does not exist.
 */
export function reconcileAfterMove(
  prevEntries: ChartEntryRef[],
  nextEntries: ChartEntryRef[],
  regions: KeyCenterRegion[],
  movedChordIds: string[]
): KeyCenterRegion[] {
  if (regions.length === 0) return regions;

  const nextChordIds = chordIdsInOrder(nextEntries);
  if (nextChordIds.length === 0) return [];

  const surviving = new Set(nextChordIds);
  const moved = new Set(movedChordIds);

  const settled: KeyCenterRegion[] = [];
  regionTerritories(prevEntries, regions).forEach(({ region, territory }) => {
    const travels = territory.length > 0 && territory.every(id => moved.has(id));

    if (travels) {
      // The region goes where its chords went: its new anchor is whichever of
      // them now comes first, since a move may also reverse a group.
      const arrived = nextChordIds.filter(id => territory.indexOf(id) !== -1);
      if (arrived.length > 0) settled.push({ ...region, startChordId: arrived[0] });
      return;
    }

    const stayed = territory.find(id => !moved.has(id) && surviving.has(id));
    if (stayed !== undefined) settled.push({ ...region, startChordId: stayed });
  });

  return mergeAdjacentRegions(regionsInChartOrder(nextEntries, settled));
}

/**
 * Sets a manual key center starting at one chord, replacing whatever region
 * already started there. It is kept even when the key before it is the same:
 * the user set it here.
 */
export function setKeyCenterAtChord(
  entries: ChartEntryRef[],
  regions: KeyCenterRegion[],
  chordId: string,
  keyCenter: KeyCenter
): KeyCenterRegion[] {
  if (chordIdsInOrder(entries).indexOf(chordId) === -1) return regions;

  const withoutExisting = regions.filter(region => region.startChordId !== chordId);
  const added: KeyCenterRegion = { startChordId: chordId, keyCenter, source: 'manual' };
  return mergeAdjacentRegions(
    regionsInChartOrder(entries, [...withoutExisting, added])
  );
}

/**
 * "None": from this chord on the chart's global key applies again, whatever it
 * is, while the chords before keep the key they had.
 *
 * This is the counterpart to setKeyCenterAtChord — "none" rather than a key.
 * The marker is a place in the song and is kept even where no local key is in
 * force before it, so a key set earlier later still ends here.
 */
export function setNoKeyCenterAtChord(
  entries: ChartEntryRef[],
  regions: KeyCenterRegion[],
  chordId: string
): KeyCenterRegion[] {
  if (chordIdsInOrder(entries).indexOf(chordId) === -1) return regions;

  const withoutExisting = regions.filter(region => region.startChordId !== chordId);
  const ended: KeyCenterRegion = { startChordId: chordId, keyCenter: null, source: 'manual' };
  return mergeAdjacentRegions(
    regionsInChartOrder(entries, [...withoutExisting, ended])
  );
}

/**
 * "Clear": the key center starting at one chord goes, and the key before it
 * carries on through its chords. Distinct from "none" (the UI's "use global
 * key"), which hands them to the global key and stays. Auto regions left side
 * by side in one key merge.
 */
export function clearKeyCenterAtChord(
  entries: ChartEntryRef[],
  regions: KeyCenterRegion[],
  chordId: string
): KeyCenterRegion[] {
  if (!regions.some(region => region.startChordId === chordId)) return regions;
  return mergeAdjacentRegions(
    regionsInChartOrder(entries, regions.filter(region => region.startChordId !== chordId))
  );
}
